import { AfterViewInit, Component, OnDestroy, effect, inject } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DialogConfirmComponent } from '@components/dialogs/dialog-confirm/dialog-confirm.component'
import { DialogWarnComponent } from '@components/dialogs/dialog-warn/dialog-warn.component'
import { MAP_OPTIONS } from '@core/constants/constant.data'
import { GeojsonPathElement } from '@core/models/geojson-edition.model'
import { LayerUploaded } from '@core/models/layers.model'
import { GeojsonEditorService } from '@core/services/geojson-editor.service'
import { GeojsonService } from '@core/services/geojson.service'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { MapService } from '@core/services/map.service'
import { bbox } from '@turf/bbox'

import * as L from 'leaflet'

@Component({
  selector: 'app-dialog-geojson-editor',
  templateUrl: './dialog-geojson-editor.component.html',
  styleUrl: './dialog-geojson-editor.component.scss',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatTooltipModule],
})
export class DialogGeojsonEditorComponent implements AfterViewInit, OnDestroy {
  geojsonEditorService = inject(GeojsonEditorService)

  data = inject(MAT_DIALOG_DATA) as { layer: LayerUploaded }

  private readonly _geojsonService = inject(GeojsonService)
  private readonly _mapService = inject(MapService)
  private readonly _confirmDialog = inject(MatDialog)
  private readonly _dialogRef = inject(MatDialogRef<DialogGeojsonEditorComponent>)
  private readonly _uploadedLayersService = inject(UploadedLayersService)

  private _currentLayer: L.Layer | null = null
  private _map: L.Map | null = null

  constructor() {
    // Update map when geojson or sld changes
    effect(async () => {
      const geojson = this.geojsonEditorService.geojson$()
      const sld = this.geojsonEditorService.sld$()
      if (!geojson || !this._map) return

      await this._addGeoJsonLayer(geojson, sld)
      const lastClosedPopup$ = this.geojsonEditorService.lastClosedPopup$()

      // Open pop-up of last closed popup if there is one
      if (lastClosedPopup$ !== null && lastClosedPopup$ !== undefined) {
        this.openFeaturePopup(lastClosedPopup$)
      }
    })
  }

  async ngAfterViewInit() {
    await this._initMap()
  }

  ngOnDestroy() {
    if (this._map) {
      this._map.remove()
      this._map = null
    }
    this.geojsonEditorService.clear()
  }

  close() {
    if (this.geojsonEditorService.changesList$().length > 0) {
      const confirmDialogRef = this._confirmDialog.open(DialogWarnComponent, {
        data: {
          title: 'Unsaved changes',
          message: 'Are you sure you want to close? You will lose all unsaved modifications.',
          cancelText: 'Continue edition',
          confirmText: 'Cancel edition',
        },
      })

      confirmDialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this._dialogRef.close({ saved: false })
        }
      })
    } else {
      this._dialogRef.close({ saved: false })
    }
  }

  save() {
    const updatedGeojson = this.geojsonEditorService.geojson$()
    const uploadId = this.data.layer.id

    if (!uploadId || !updatedGeojson) {
      console.error('Missing upload ID or GeoJSON data')
      return
    }

    if (this.geojsonEditorService.changesList$().length === 0) {
      this._dialogRef.close({ saved: false })
      return
    }

    const confirmDialogRef = this._confirmDialog.open(DialogConfirmComponent, {
      data: {
        title: 'Confirm layer overwrite',
        message: 'Are you sure you want to override the existing layer with your modifications?',
      },
    })

    confirmDialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        await this.geojsonEditorService.updateGeoJSON(uploadId, updatedGeojson)
        this._dialogRef.close({ saved: true })
      }
    })
  }

  saveNew() {
    const updatedGeojson = this.geojsonEditorService.geojson$()

    if (!updatedGeojson) {
      console.error('No GeoJSON data')
      return
    }

    if (this.geojsonEditorService.changesList$().length === 0) {
      this._dialogRef.close()
      return
    }

    const confirmDialogRef = this._confirmDialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Save as new personal layer',
        message: 'This will create a new copy of the GeoJSON layer with your modifications.',
        confirmText: 'Create copy',
      },
    })

    confirmDialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        const filename = this.data.layer.name

        const geojsonString = JSON.stringify(updatedGeojson, null, 2)
        const blob = new Blob([geojsonString], { type: 'application/geo+json' })
        const file = new File([blob], filename, { type: 'application/geo+json' })

        const result = await this._uploadedLayersService.addPersonalLayer(
          file,
          {
            workspacename: this.data.layer.layer,
            layer_type: this.data.layer.layer_type,
          },
          [], // empty share list
        )
        if (result) {
          this.geojsonEditorService.clear()
          this._dialogRef.close({ savedAsNew: true, id: result[0].id }) //@todo, fix for arrays
        }
      }
    })
  }

  private async _initMap() {
    const mapDiv = document.getElementById('geojson-dialog-map')
    if (!mapDiv) return

    this._map = L.map(mapDiv, MAP_OPTIONS)

    // Add a fresh copy of the current background layer
    const bgLayer = this._mapService.getBackgroundLayerCopy()
    if (bgLayer) {
      bgLayer.addTo(this._map)
    }

    // Center the map on the geometry using Turf.js
    const geojson = this.geojsonEditorService.geojson$()
    if (geojson && geojson.features && geojson.features.length > 0) {
      const geojsonBbox = bbox(geojson)
      const bounds = L.latLngBounds(
        [geojsonBbox[1], geojsonBbox[0]], // southwest (minLat, minLng)
        [geojsonBbox[3], geojsonBbox[2]], // northeast (maxLat, maxLng)
      )
      this._map.fitBounds(bounds, { maxZoom: 16, padding: [20, 20] })
    }

    await this._addGeoJsonLayer(geojson, this.geojsonEditorService.sld$())
  }

  private async _addGeoJsonLayer(geojson: L.GeoJSON, sld: string) {
    if (!this._map) return

    if (this._currentLayer) {
      this._map.removeLayer(this._currentLayer)
      this._currentLayer = null
    }

    let layerGroup: L.LayerGroup
    let featureLayerMap: Map<number, L.Layer> | undefined

    if (sld) {
      ;({ layerGroup, featureLayerMap } =
        await this._geojsonService.buildStylizedEditableGeoJsonLayerGroup(geojson, sld))
    } else {
      ;({ layerGroup, featureLayerMap } =
        this._geojsonService.buildEditableGeoJSONLayerGroup(geojson))
    }

    if (layerGroup && featureLayerMap) {
      layerGroup.addTo(this._map)
      this._currentLayer = layerGroup
      this.geojsonEditorService.featureLayerMap = featureLayerMap
    }
  }

  openFeaturePopup(featureIndex: number) {
    const layer = this.geojsonEditorService.featureLayerMap.get(featureIndex)
    if (layer) layer.openPopup()
  }

  displayPath(path: GeojsonPathElement[]): string {
    return path
      .map((segment, i) => {
        if (this.geojsonEditorService.isArrayIndex(segment)) {
          return `[${segment.index}]`
        }
        return i === 0 ? String(segment) : `.${segment}`
      })
      .join('')
  }
}
